import React from 'react'
import { Typography, Box, Accordion, AccordionSummary, AccordionDetails, Link } from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { LytReservesCTO } from '@shared/LytReservesCTO'
import { PoolOffchainDataCTO } from '@shared/PoolOffchainDataCTO'
import { erc20WeiToCommaDecString, ERC20Wei } from '@shared/conversions'

interface ReserveRowData extends LytReservesCTO {
  sharePercent: number
  token: string
  yieldType: string
  lastUpdated: string
  indicativeRate?: string
}

interface LytReservesTableProps {
  reserves: (LytReservesCTO & { indicativeRate?: string; yieldType?: string })[]
  poolOffchainData?: PoolOffchainDataCTO
}

const _formatCurrency = (value: ERC20Wei) => {
  const numValue = parseFloat(erc20WeiToCommaDecString(value))
  const formatted = `$${numValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
  return formatted
}

const _formatAPY = (apy: string) => {
  return `${parseFloat(apy).toFixed(2)}%`
}

const formatSharePercent = (percent: number) => {
  return `${percent.toFixed(1)}%`
}

export function LytReservesTable({ reserves, poolOffchainData }: LytReservesTableProps) {
  const totalReserveValue = reserves.reduce((sum, reserve) => {
    const value = parseFloat(erc20WeiToCommaDecString(reserve.currentAssetValue))
    return sum + value
  }, 0)

  const enrichedReserves: ReserveRowData[] = reserves
    .map((reserve) => {
      const assetValue = parseFloat(erc20WeiToCommaDecString(reserve.currentAssetValue))
      const sharePercent = totalReserveValue > 0 ? (assetValue / totalReserveValue) * 100 : 0

      return {
        ...reserve,
        sharePercent,
        token: reserve.poolToken,
        yieldType: (() => {
          if ((reserve as any).displayName.includes('Liquidity Buffer')) {
            return 'N/A'
          }
          return reserve.yieldType || poolOffchainData?.yieldType || 'N/A'
        })(),
        lastUpdated: new Date().toISOString().split('T')[0],
      }
    })
    .sort((a, b) => b.sharePercent - a.sharePercent) // Sort by share percentage, largest to smallest

  return (
    <Box sx={{ width: '100%' }}>
      {/* Column Headers */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '2fr 1fr 1fr 1fr',
          gap: 6,
          px: 3,
          py: 2,
          mb: 2,
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'rgba(255, 255, 255, 0.7)' }}>
          Asset Type
        </Typography>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'rgba(255, 255, 255, 0.7)', textAlign: 'left' }}>
          APY
        </Typography>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'rgba(255, 255, 255, 0.7)', textAlign: 'left' }}>
          Value
        </Typography>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'rgba(255, 255, 255, 0.7)', textAlign: 'left' }}>
          Share %
        </Typography>
      </Box>

      {/* Accordion Items */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {enrichedReserves.map((reserve, index) => (
          <Accordion
            key={reserve.id || index}
            sx={{
              backgroundColor: '#1a1a1a',
              border: '1px solid #1e2838',
              borderRadius: '8px !important',
              '&:before': { display: 'none' },
              '&.Mui-expanded': {
                margin: 0,
                mb: 1,
              },
              '&:hover': {
                backgroundColor: '#1f1f1f',
              },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />}
              sx={{
                px: 3,
                py: 1,
                minHeight: 64,
                '&.Mui-expanded': {
                  minHeight: 64,
                },
                '& .MuiAccordionSummary-content': {
                  margin: 0,
                  '&.Mui-expanded': {
                    margin: 0,
                  },
                },
              }}
            >
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '2fr 1fr 1fr 1fr',
                  gap: 6,
                  width: '100%',
                  alignItems: 'center',
                }}
              >
                <Typography variant="body1" sx={{ fontWeight: 500, color: 'white' }}>
                  {(reserve as any).displayName || reserve.poolName?.trim() || 'Liquidity Buffer (USDC)'}
                </Typography>
                <Typography variant="body1" sx={{ color: 'white', textAlign: 'center' }}>
                  {reserve.indicativeRate ? `${(parseFloat(reserve.indicativeRate) / 100).toFixed(2)}%` : '0.00%'}
                </Typography>
                <Typography variant="body1" sx={{ color: 'white', textAlign: 'center' }}>
                  {erc20WeiToCommaDecString(reserve.currentAssetValue)}
                </Typography>
                <Typography variant="body1" sx={{ color: 'white', textAlign: 'center' }}>
                  {formatSharePercent(reserve.sharePercent)}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 3, pb: 3 }}>
              <Box
                sx={{
                  borderRadius: '8px',
                  p: 3,
                  backgroundColor: '#1a1a1a',
                }}
              >
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4 }}>
                  <Box>
                    <Typography variant="body2" sx={{ display: 'block', mb: 1, color: 'rgba(255, 255, 255, 0.6)' }}>
                      Token
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500, color: 'white' }}>
                      {reserve.token}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" sx={{ display: 'block', mb: 1, color: 'rgba(255, 255, 255, 0.6)' }}>
                      Last Updated
                    </Typography>
                    <Typography variant="body1" sx={{ color: 'white' }}>
                      {reserve.lastUpdated}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" sx={{ display: 'block', mb: 1, color: 'rgba(255, 255, 255, 0.6)' }}>
                      Yield Type
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500, color: 'white' }}>
                      {(reserve as any).displayName?.includes('Liquidity Buffer') ? 'N/A' : reserve.yieldType}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" sx={{ display: 'block', mb: 1, color: 'rgba(255, 255, 255, 0.6)' }}>
                      Vault Address
                    </Typography>
                    <Link
                      href={`https://snowtrace.io/address/${reserve.poolAddr}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        color: '#E769A6',
                        textDecoration: 'none',
                        fontFamily: 'monospace',
                        fontSize: '0.85rem',
                        wordBreak: 'break-all',
                        '&:hover': {
                          textDecoration: 'underline',
                        },
                      }}
                    >
                      {reserve.poolAddr}
                    </Link>
                  </Box>
                </Box>
              </Box>
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>

      {/* <Box sx={{ mt: 6 }}>
        <Typography variant="h6" sx={{ color: 'white', mb: 3 }}>
          Reserve Reports
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: 2,
          }}
        >
          {[
            { name: 'Reserve Report - March 2024', url: '#' },
            { name: 'Reserve Report - February 2024', url: '#' },
            { name: 'Reserve Report - January 2024', url: '#' },
            { name: 'Reserve Report - December 2023', url: '#' },
            { name: 'Reserve Report - November 2023', url: '#' },
            { name: 'Reserve Report - October 2023', url: '#' },
          ].map((report, index) => (
            <Box
              key={index}
              component={Link}
              href={report.url}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 3,
                py: 2,
                backgroundColor: '#1a1a1a',
                border: '1px solid #1e2838',
                borderRadius: 1,
                textDecoration: 'none',
                cursor: 'pointer',
                transition: 'all 0.2s',
                '&:hover': {
                  backgroundColor: '#1f1f1f',
                  borderColor: '#d95f80',
                },
              }}
            >
              <Typography variant="body2" sx={{ color: '#d95f80', fontSize: '14px' }}>
                {report.name}
              </Typography>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#d95f80" strokeWidth="2">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" />
              </svg>
            </Box>
          ))}
        </Box>
      </Box> */}
    </Box>
  )
}
